import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiBook,
  FiHeadphones,
  FiGlobe,
  FiExternalLink,
  FiUsers,
  FiTrendingUp,
  FiCloud,
  FiCheckCircle,
  FiFileText,
  FiMail
} = FiIcons;

const ResourcesSection = () => {
  const resources = [
    {
      icon: FiHeadphones,
      title: 'Podcast: Skilling och kunskapsdrivna organisationer',
      description: 'Lyssna på hur större organisationer bör jobba med reskilling och upskilling i IT-branschen.',
      link: 'https://youtu.be/SlhVR9kuBNg?si=v6OhYJASJu2YzqYW',
      color: 'from-green-500 to-teal-500'
    },
    {
      icon: FiCloud,
      title: 'AZ-900 Studieguide för Azure',
      description: 'Ladda ner min gratisguide på hur du börjar med Azure via AZ-900 certifieringen och ta dina första steg mot molnet.',
      link: 'https://jonasjaani.se/az-900-studieguide-azure-microsoft-studieguide-certifering/',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: FiTrendingUp,
      title: 'Framtidens karriär - Mailinglistan',
      description: 'Gå med i mailinglistan för en framgångsrik IT-karriär. Få de senaste insikterna och tipsen direkt i din inkorg.',
      link: 'https://sendfox.com/lp/3oxow4',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: FiCheckCircle,
      title: 'Utvärdering av dataplattforms-kompetens',
      description: 'Testa dig själv idag! Få en snabb utvärdering av din kompetens inom dataplattformar och se var du bör fokusera framåt.',
      link: 'https://jonasjaani.se/utvardering-av-kompetens-inom-dataplattformar/',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      icon: FiFileText,
      title: 'Mänskliga färdigheter i framtidens IT',
      description: 'Mänskliga färdigheter skapar framtidens arbetssätt. Ladda ner guiden om hur mjuka värden blir avgörande i en AI-driven värld.',
      link: 'https://digitalatips.se/wp-content/uploads/2026/01/Manskliga-fardigheter-skapar-framtidens-arbetssatt_it.pdf',
      color: 'from-amber-500 to-orange-500'
    },
    {
      icon: FiMail,
      title: 'Jonas Jaani nyhetsbrev',
      description: 'Få gratis tillgång till guider, podcasts, chatbots och insikter som hjälper dig navigera inom AI och IT-branschen.',
      link: 'https://jonasjaani.se/nyhetsbrev/',
      color: 'from-blue-600 to-indigo-600'
    },
    {
      icon: FiBook,
      title: 'Bok: Skapa framgångsrika traineeprogram',
      description: 'För coacher och organisationer som vill höja kunskapen och skapa effektiva utbildningsprogram.',
      link: 'https://www.keyfutureskills.com/product/skapa-framgangsrika-traineeprogram/',
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: FiBook,
      title: 'Kickstarta din konsultkarriär - Bok',
      description: 'Guiden du önskar att du fick dag 1. Min nya guide för nya IT-konsulter är din mentor i fickformat. Här får du verktygen för att snabbt bygga förtroende, hantera komplexa uppdrag och leverera som ett proffs från start. Perfekt för dig som är ny i branschen, eller för chefen som vill ge sina juniora konsulter bästa möjliga onboarding.',
      link: 'https://buymeacoffee.com/jonasjaani/e/530281',
      color: 'from-violet-500 to-purple-500'
    },
    {
      icon: FiGlobe,
      title: 'JonasJaani.se',
      description: 'Läs mina senaste tankar och tips om IT-karriär, teknisk utveckling och branschtrender.',
      link: 'https://www.jonasjaani.se',
      color: 'from-blue-500 to-indigo-500'
    }
  ];

  const keyTakeaways = [
    {
      icon: FiUsers,
      title: 'Mjuka färdigheter är avgörande',
      description: 'Kommunikation, anpassningsförmåga och affärsförståelse är lika viktigt som teknisk kompetens.'
    },
    {
      icon: FiTrendingUp,
      title: 'Livslångt lärande är nödvändigt',
      description: 'Med AI:s utveckling måste du ständigt uppdatera dina kunskaper och vara nyfiken på nytt.'
    },
    {
      icon: FiBook,
      title: 'Fokusera på en plattform först',
      description: 'Lär dig Azure eller Microsoft-stacken grundligt, sedan kan du enklare lära dig andra.'
    }
  ];

  return (
    <section id="resurser" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.6}}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Ytterligare{' '}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              resurser
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Fördjupa din kunskap med dessa resurser för din IT-karriär
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-16">
          {resources.map((resource, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300"
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.6, delay: index * 0.1}}
              whileHover={{scale: 1.02, y: -5}}
            >
              <div className={`bg-gradient-to-r ${resource.color} p-6 text-white`}>
                <SafeIcon icon={resource.icon} className="w-8 h-8 mb-4" />
                <h3 className="text-xl font-bold mb-2">{resource.title}</h3>
              </div>
              <div className="p-6">
                <p
                  style={{color: 'rgb(75,85,99)'}}
                  className="text-gray-600 mb-6 leading-relaxed flex-grow"
                >
                  {resource.description}
                </p>
                <motion.a
                  href={resource.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-blue-600 font-semibold hover:text-blue-800 transition-colors duration-200"
                  whileHover={{x: 5}}
                >
                  <span>Läs mer</span>
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                </motion.a>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="bg-white rounded-2xl shadow-xl p-8"
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.6}}
        >
          <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">
            Viktiga slutsatser för din IT-karriär
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {keyTakeaways.map((takeaway, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{opacity: 0, y: 20}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.6, delay: index * 0.1}}
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={takeaway.icon} className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">{takeaway.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{takeaway.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ResourcesSection;